import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { TechLayout } from '../../components/tech/TechLayout';
import { H3, Lead, Purpose, TCap, Fig, SpecTable, Video, Chips } from '../../components/tech/TechUI';
import { BenchTable, References } from '../../components/tech/BenchTable';
import { AlignChart, TrajectoryChart } from '../../components/tech/BenchmarkCharts';
import { AutoVideo, ZoomImage } from '../../components/tech/Zoom';
import { T } from '../../data/techSections';
import { CAPTURE_ROWS, SLAM_ROWS, DEPTH_ROWS, PIPELINE, NEEDS, STACK, SLAM_VIDEOS, DEPTH_VIDEOS } from './modelsData';

const Sub = ({ children }) => <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/35 mt-8 mb-3">{children}</p>;

const Pipeline = () => (
  <div className="space-y-3" data-testid="model-pipeline">
    {PIPELINE.map(([inp, [kind, name, desc], out]) => (
      <div key={name} className="grid md:grid-cols-[1fr_auto_1.3fr_auto_1fr] gap-2 md:gap-3 items-stretch">
        <div className="rounded-xl border border-white/10 bg-[#141414] p-4 text-xs text-white/55 leading-relaxed"><b className="block text-sm text-white/90 mb-1">{inp[0]}</b>{inp[1]}</div>
        <div className="hidden md:flex items-center text-[#FF7A00]">→</div>
        <div className="rounded-xl border border-[#FF7A00]/40 bg-[#FF7A00]/[0.06] p-4 text-xs text-white/60 leading-relaxed">
          <span className="block font-mono text-[10px] tracking-[0.16em] uppercase text-[#FF7A00]/80">{kind}</span>
          <b className="block text-sm text-white mb-1">{name}</b>{desc}
        </div>
        <div className="hidden md:flex items-center text-[#FF7A00]">→</div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-4 text-xs text-white/55 leading-relaxed"><b className="block text-sm text-white/90 mb-1">{out[0]}</b>{out[1]}</div>
      </div>
    ))}
  </div>
);

const needsRows = [...NEEDS, ['Variety', <>Different workplaces, lighting, tools, gloves and hands. <Link to="/data/showcase" className="text-[#FF7A00] font-semibold hover:text-[#FFD000]">See the Data Showcase</Link></>]];

export default function ModelsPage() {
  return (
    <TechLayout slug="benchmarks" lead={<Lead>JanData Cap footage and its IMU data, run through standard open-source models, give 3D hand pose, the head's path through the room, a 3D map of the workplace and dense depth. We put our six-camera video through our quality pipeline, ran the models on the best-quality recordings, and measured how reliably they run. Hand pose is shown on recordings from a wide range of workplaces plus a welding close-up; one PCB soldering recording is followed through tracking, mapping and depth in detail.</Lead>}>
      <Purpose>Each result shows whether a model runs reliably on the data and how stable its output is. There is no ground truth here (no motion-capture or depth sensor), so these are not accuracy figures.</Purpose>

      <H3 id="pipeline">Pipeline</H3>
      <Pipeline />

      <H3 id="capture-quality">Capture quality</H3>
      <BenchTable rows={CAPTURE_ROWS} testid="table-capture-quality" />
      <TCap title="">Capture integrity of the benchmark recordings, across all six cameras and three IMUs of each.</TCap>
      <Sub>Chart</Sub>
      <Fig cap="One recording (Cocktail bar): how fast the head turns according to the gyroscope (blue, degrees per second) and how fast the side camera's image moves (orange, pixels per frame). The two rise and fall together; agreement 1.00.">
        <AlignChart />
      </Fig>

      <H3 id="hand-pose">Hand pose</H3>
      <Purpose>The front camera's footage goes through WiLoR; hands are tracked through each clip and the keypoints smoothed. The video below strings together short clips from recordings across many workplaces.</Purpose>
      <Fig cap="WiLoR hand tracking on recordings across industries. Blue is the right hand, orange the left.">
        <Video name="videos/hands-reel" testid="video-hands-reel" />
      </Fig>
      <TCap title="What hand tracking needs.">What hand pose depends on, and what our data gives.</TCap>
      <SpecTable head={['Needs', 'JanData data']} rows={needsRows} testid="table-hand-needs" />
      <TCap title="Close-up.">One recording in detail: the video, then single frames with the 21 keypoints on each hand.</TCap>
      <Fig cap="WiLoR hand tracking on a welding and metal-press recording (industrial manufacturing). Blue is the right hand, orange the left.">
        <Video name="videos/hands-welding" testid="video-hands-welding" />
        <div className="grid grid-cols-3 gap-2 mt-3">
          {['10', '45', '80'].map((n) => <ZoomImage key={n} src={T(`hands/welding-${n}.jpg`)} alt="Hand keypoints at a metal press" testid={`hands-welding-${n}`} />)}
        </div>
      </Fig>

      <H3 id="head-tracking-orb-slam3">Head tracking: ORB-SLAM3</H3>
      <BenchTable rows={SLAM_ROWS} testid="table-slam" />
      <TCap title="">ORB-SLAM3 (monocular, fisheye) on the benchmark recordings.</TCap>
      <Sub>Charts</Sub>
      <Fig cap="The camera's path over one minute as ORB-SLAM3 estimated it, one recording per workplace. Drag to rotate. A single camera gives no real-world scale, so the axes are in map units.">
        <TrajectoryChart />
      </Fig>
      <Fig cap="Replays of benchmark recordings, keyframe by keyframe: the features ORB-SLAM3 tracks, depth for the same frame (Depth Anything V2, metric), and the head's path through the map from above. Click a video to enlarge.">
        <div className="grid md:grid-cols-3 gap-4">
          {SLAM_VIDEOS.map(([id, name, stats]) => <AutoVideo key={id} name={id} testid={`slam-video-${id}`} caption={<><b className="text-white/80">{name}</b> · {stats}</>} />)}
        </div>
      </Fig>
      <TCap title="Worked example.">A PCB soldering recording, followed from tracked features to depth and a 3D map in metres.</TCap>
      <Fig cap="Keyframe by keyframe: the features ORB-SLAM3 tracks, the depth Depth Anything V2 predicts, and the head's path through the map from above.">
        <Video name="slam-soldering-replay" testid="video-slam-soldering" />
      </Fig>
      <Fig cap="ORB-SLAM3 tracked features on keyframes from the start, middle and end of the clip. Features sit on fixed structure: benches, shelves, walls and fixtures.">
        <ZoomImage src={T('slam-soldering-features.jpg')} alt="Tracked features on three keyframes" testid="figure-slam-features" />
      </Fig>
      <Fig cap="Rectified keyframes (top) and Depth Anything V2 metric depth (bottom; warm is near, cool is far).">
        <ZoomImage src={T('slam-soldering-depth.jpg')} alt="Rectified keyframes and their depth" testid="figure-slam-depth" />
      </Fig>
      <Fig cap="The 3D map and the head path in metres, held upright using the IMU's gravity estimate. Colour shows height.">
        <ZoomImage src={T('slam-soldering-map.png')} alt="Metric 3D map of the workshop with the head path" testid="figure-slam-map" />
      </Fig>

      <H3 id="depth-video-depth-anything">Depth: Video Depth Anything</H3>
      <BenchTable rows={DEPTH_ROWS} testid="table-depth" />
      <TCap title="">Video Depth Anything (Small) on the benchmark recordings, at 10 frames a second.</TCap>
      <Sub>Video</Sub>
      <Fig cap="Video Depth Anything on 20 seconds of each recording, frame by frame: the front-left camera and the depth the model predicts for it (bright is near, dark is far). Watch the hands and tools stay sharp and steady in depth as they move.">
        <div className="grid md:grid-cols-3 gap-4">
          {DEPTH_VIDEOS.map(([id, name]) => <AutoVideo key={id} name={id} testid={`depth-video-${id}`} caption={<><b className="text-white/80">{name}</b> · camera (left) and predicted depth (right)</>} />)}
        </div>
      </Fig>

      <H3 id="works-across-the-robot-learning-stack">Works across the robot-learning stack</H3>
      <Purpose>Synchronised cameras and IMU, calibrated fisheye lenses and standard formats: our data feeds the models robot training uses, and more.</Purpose>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="model-stack">
        {STACK.map(([title, items, ran]) => (
          <div key={title} className="rounded-2xl border border-white/10 bg-[#141414] p-4">
            <h4 className="text-sm font-semibold text-white/90 mb-3">{title}</h4>
            <Chips items={items} ran={ran} />
          </div>
        ))}
      </div>
      <div className="text-xs text-white/45 mt-3 flex items-center gap-2"><Chips items={['Highlighted']} ran={['Highlighted']} /> models were run on our recordings for this report.</div>

      <p className="text-xs text-white/45 mt-12">Each good range names its source. Where no published standard exists, it is our own benchmark threshold and is marked as such. One frame at 30 fps is 33 ms.</p>
      <References />

      <Link to="/contact" className="inline-flex items-center gap-2 btn-gradient px-7 py-3.5 font-semibold text-sm rounded-full mt-10" data-testid="tech-contact-cta">
        Request data <ArrowRight size={14} />
      </Link>
    </TechLayout>
  );
}
